// The event catalogue (contracts/events.md) as runtime schemas.
//
// Every state change in the platform is one of these events, validated here
// before it is appended to the ledger. Add the event to contracts/events.md,
// then here, then build the feature that emits it (CLAUDE.md rule 3).
import { z } from "zod";
import { isoDateTimeSchema, severitySchema, sourceSchema, uuidSchema } from "../schemas/common.js";

export const evidenceKindSchema = z.enum([
  "photo",
  "document",
  "read_record",
  "field_record",
  "correspondence",
  "market_response",
  "gate_record",
]);
export type EvidenceKind = z.infer<typeof evidenceKindSchema>;

const attribute = z.string().min(1);
const money = z.never(); // No money figures anywhere in this build; the type exists so a payload cannot smuggle one.
void money;

/** Where the fact came from within its source: an import line, a job reference, a user id. */
const sourceRef = z.string().optional();

const attributeChange = z.object({
  attribute,
  old: z.unknown(),
  new: z.unknown(),
  confidence: z.number().min(0).max(1),
  sourceRef,
});

const locationType = z.enum([
  "internal",
  "external_chamber",
  "external_boundary_box",
  "other",
  "unknown",
]);

/** Payload schema per event type. The key is the event type exactly as catalogued. */
export const eventPayloadSchemas = {
  // 1. Import
  ImportStarted: z.object({
    fileName: z.string(),
    entityKind: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    mappingId: uuidSchema.nullable(),
  }),
  ImportQuarantined: z.object({
    countsByReason: z.record(z.number().int().nonnegative()),
    sampleLines: z.array(z.number().int().positive()),
  }),
  ImportCommitted: z.object({
    rowsCommitted: z.number().int().nonnegative(),
    eventsEmitted: z.number().int().nonnegative(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  }),

  // 2. Registry
  TradingPartyRegistered: z.object({
    name: z.string().min(1),
    role: z.enum(["wholesaler", "retailer", "self_supplier", "contractor"]),
    marketPartyId: z.string().nullable(),
    sourceRef,
  }),
  SupplyPointRegistered: z.object({
    spid: z.string(),
    coreSpid: z.string(),
    service: z.enum(["water", "sewerage"]),
    premisesId: uuidSchema,
    wholesalerId: uuidSchema,
    retailerId: uuidSchema.nullable(),
    retailerName: z.string().nullable().optional(),
    occupancyStatus: z.enum(["occupied", "vacant", "unknown"]).optional(),
    occupancySince: z.string().date().nullable().optional(),
    marketStatus: z.enum(["registered", "partially_registered", "deregistered"]).optional(),
    confidence: z.number().min(0).max(1).optional(),
    sourceRef,
  }),
  SupplyPointAttributeChanged: attributeChange,
  OccupancyChanged: z.object({
    from: z.enum(["occupied", "vacant", "unknown"]),
    to: z.enum(["occupied", "vacant", "unknown"]),
    effectiveDate: z.string().date(),
    confidence: z.number().min(0).max(1).optional(),
    sourceRef,
  }),
  PremisesRegistered: z.object({
    address: z.array(z.string()),
    postcode: z.string(),
    uprn: z.string().nullable(),
    useClass: z.string().nullable().optional(),
    location: z.object({ lat: z.number(), lon: z.number() }).nullable().optional(),
    externalRef: z.string().optional(),
    confidence: z.number().min(0).max(1).optional(),
    sourceRef,
  }),
  PremisesAttributeChanged: attributeChange,
  PremisesMerged: z.object({ survivorId: uuidSchema, mergedId: uuidSchema, reason: z.string() }),
  MeterInstalled: z.object({
    serial: z.string(),
    manufacturer: z.string().nullable(),
    sizeMm: z.number().int().positive().nullable(),
    dials: z.number().int().positive().nullable(),
    readMethod: z.enum(["smart", "amr", "manual"]),
    installedOn: z.string().date(),
    supplyPointId: uuidSchema,
    initialRead: z.number().nonnegative().nullable(),
    model: z.string().nullable().optional(),
    locationType: locationType.optional(),
    locationDescription: z.string().nullable().optional(),
    gridRef: z.string().nullable().optional(),
    accessNotes: z.string().nullable().optional(),
    removedOn: z.string().date().nullable().optional(),
    status: z.enum(["active", "removed", "faulty_suspected", "faulty_confirmed"]).optional(),
    externalRef: z.string().optional(),
    confidence: z.number().min(0).max(1).optional(),
    sourceRef,
  }),
  MeterExchanged: z.object({
    removedMeterId: uuidSchema,
    finalRead: z.number().nonnegative(),
    newSerial: z.string(),
    newAttributes: z.record(z.unknown()),
    exchangedOn: z.string().date(),
    fieldJobId: uuidSchema.nullable(),
  }),
  MeterRemoved: z.object({
    removedOn: z.string().date(),
    finalRead: z.number().nonnegative().nullable(),
    reason: z.string(),
  }),
  MeterAttributeChanged: attributeChange,
  MeterLocationVerified: z.object({
    locationType: z.string(),
    gridRef: z.string().nullable(),
    description: z.string(),
    accessNotes: z.string().nullable(),
  }),
  AttributeConflictDetected: z.object({
    attribute,
    values: z
      .array(z.object({ value: z.unknown(), source: sourceSchema, sourceRef: z.string() }))
      .min(2),
  }),
  AttributeConflictResolved: z.object({ attribute, chosen: z.unknown(), reason: z.string() }),

  // 3. Reads
  ReadRecorded: z.object({
    readAt: isoDateTimeSchema,
    value: z.number().nonnegative(),
    readType: z.enum(["actual", "customer", "estimated", "smart_interval", "smart_daily"]),
    sourceRef: z.string(),
  }),
  ReadRolloverConfirmed: z.object({ readId: uuidSchema, dials: z.number().int().positive() }),
  ReadQueried: z.object({ readId: uuidSchema, reason: z.string() }),
  ReadWithdrawn: z.object({ readId: uuidSchema, reason: z.string() }),

  // 4. Findings
  RuleRunStarted: z.object({
    configSnapshotHash: z.string(),
    dataAsOf: isoDateTimeSchema,
    ref: z.string().optional(),
    ruleIds: z.array(z.string()).optional(),
    trigger: z.enum(["manual", "schedule", "field_job"]).optional(),
    /** A run scoped to supply points after a field job completes (D-62); absent for a full run. */
    scope: z.object({ supplyPointIds: z.array(uuidSchema) }).optional(),
  }),
  RuleRunCompleted: z.object({
    countsByRule: z.record(z.record(z.number().int().nonnegative())),
    durationMs: z.number().int().nonnegative(),
    counts: z
      .object({
        raised: z.number().int().nonnegative(),
        updated: z.number().int().nonnegative(),
        autoCleared: z.number().int().nonnegative(),
        verified: z.number().int().nonnegative(),
        reopened: z.number().int().nonnegative(),
        clearedByEvidence: z.number().int().nonnegative().optional(),
      })
      .optional(),
    timings: z.record(z.number().int().nonnegative()).optional(),
  }),
  FindingRaised: z.object({
    ruleId: z.string(),
    entityType: z.string(),
    entityId: uuidSchema,
    fingerprint: z.string(),
    severity: severitySchema,
    facts: z.record(z.unknown()),
    ownerPartyId: uuidSchema.nullable(),
    ownerTeamId: uuidSchema.nullable(),
    // Phase 2 (D-43, D-44): what the queues show, carried so a rebuild reproduces it.
    ref: z.string().optional(),
    supplyPointId: uuidSchema.nullable().optional(),
    explanation: z.string().optional(),
    slaDueAt: isoDateTimeSchema.optional(),
    impactFactors: z.record(z.unknown()).optional(),
    annualVolumeM3: z.number().nonnegative().optional(),
    runId: uuidSchema.optional(),
  }),
  FindingUpdated: z.object({
    changedFacts: z.record(z.unknown()),
    runId: uuidSchema,
    facts: z.record(z.unknown()).optional(),
    explanation: z.string().optional(),
    impactFactors: z.record(z.unknown()).optional(),
    annualVolumeM3: z.number().nonnegative().optional(),
  }),
  FindingTransitioned: z.object({
    from: z.string(),
    to: z.string(),
    reason: z.string().nullable(),
    assignee: uuidSchema.nullable(),
    reasonCode: z.string().nullable().optional(),
    ownerPartyId: uuidSchema.nullable().optional(),
    ownerTeamId: uuidSchema.nullable().optional(),
    awaitingRef: z.string().nullable().optional(),
    runId: uuidSchema.nullable().optional(),
  }),
  FindingAutoCleared: z.object({ runId: uuidSchema }),
  RuleConfigChanged: z.object({
    ruleId: z.string(),
    oldParams: z.record(z.unknown()),
    newParams: z.record(z.unknown()),
    reason: z.string(),
  }),
  FindingEvidenceLinked: z.object({
    evidenceId: uuidSchema,
    kind: evidenceKindSchema,
    linkedBy: z.string(),
    citedRead: z
      .object({ meterId: uuidSchema, readAt: isoDateTimeSchema, sourceRef: z.string() })
      .nullable(),
  }),

  // 5. Evidence and field
  EvidenceAttached: z.object({
    kind: evidenceKindSchema,
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    attachedTo: z.array(z.object({ type: z.string(), id: z.string() })).min(1),
    capturedAt: isoDateTimeSchema.nullable(),
    capturedLocation: z.object({ lat: z.number(), lon: z.number() }).nullable(),
    objectKey: z.string().optional(),
    externalRef: z.string().optional(),
    // Phase 3 (D-54 to D-57): an uploaded file's name, type, size, the scan that passed it and the EXIF check.
    fileName: z.string().optional(),
    contentType: z.string().optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    scannedAt: isoDateTimeSchema.optional(),
    scanEngine: z.string().optional(),
    locationMismatch: z.boolean().optional(),
    sourceRef,
  }),
  FieldJobRaised: z.object({
    type: z.string(),
    supplyPointId: uuidSchema.nullable(),
    meterId: uuidSchema.nullable(),
    findingId: uuidSchema.nullable(),
    assignee: uuidSchema.nullable(),
    assigneeName: z.string().nullable().optional(),
    status: z.enum(["raised", "dispatched", "completed", "aborted", "cancelled"]).optional(),
    externalRef: z.string().optional(),
    // Phase 3 (D-59): a job raised here carries its reference, due date, brief, target and who raised it.
    ref: z.string().optional(),
    dueAt: isoDateTimeSchema.optional(),
    brief: z.string().optional(),
    target: z.object({ uprn: z.string().nullable(), address: z.string() }).nullable().optional(),
    raisedBy: z.string().optional(),
    sourceRef,
  }),
  FieldJobDispatched: z.object({ channel: z.enum(["csv", "field_app"]), batchId: z.string() }),
  FieldJobCompleted: z.object({
    outcome: z.string(),
    result: z.record(z.unknown()),
    sourceRef,
  }),
  FieldJobAborted: z.object({ reason: z.enum(["no_access", "unsafe", "not_found", "other"]) }),

  // 7. Gate and submissions
  SubmissionGated: z.object({
    target: z.enum(["cmos", "read_hub", "bilaterals_hub"]),
    kind: z.string(),
    itemHash: z.string(),
    outcome: z.enum(["passed", "normalised", "held"]),
    checks: z.array(
      z.object({
        id: z.string(),
        result: z.enum(["pass", "fail", "not_applicable"]),
        reason: z.string().nullable(),
      }),
    ),
    normalisation: z.array(
      z.object({ field: z.string(), before: z.unknown(), after: z.unknown() }),
    ),
    configVersion: z.number().int(),
  }),
  SubmissionReleased: z.object({ reason: z.string(), override: z.boolean() }),
  SubmissionWithdrawn: z.object({ reason: z.string() }),
  SubmissionSent: z.object({ target: z.string(), port: z.string(), simulated: z.boolean() }),
  SubmissionAcknowledged: z.object({ targetReference: z.string(), simulated: z.boolean() }),
  SubmissionRejected: z.object({
    reasonCode: z.string(),
    message: z.string(),
    simulated: z.boolean(),
  }),

  // 8. Bilaterals
  BilateralDrafted: z.object({
    processCode: z.string(),
    direction: z.enum(["outbound", "inbound"]),
    fields: z.record(z.unknown()),
    findingId: uuidSchema.nullable(),
  }),
  BilateralMarkedReady: z.object({ complete: z.boolean(), missingFields: z.array(z.string()) }),
  BilateralSent: z.object({ port: z.string(), simulated: z.boolean() }),
  BilateralResponded: z.object({ response: z.record(z.unknown()), simulated: z.boolean() }),
  BilateralClosed: z.object({ outcome: z.string() }),

  // 9. Install Quality
  InstallRecorded: z.object({
    installRef: z.string(),
    jobType: z.enum(["new", "exchange"]),
    spid: z.string(),
    installerId: uuidSchema,
    installedAt: isoDateTimeSchema,
    scannedSerial: z.string().nullable(),
    typedSerial: z.string().nullable(),
    gps: z
      .object({ lat: z.number(), lon: z.number(), accuracyM: z.number().nonnegative() })
      .nullable(),
    initialRead: z.number().nonnegative().nullable(),
    oldSerial: z.string().nullable(),
    oldFinalRead: z.number().nonnegative().nullable(),
    location: z.object({
      type: z.string().nullable(),
      gridRef: z.string().nullable(),
      description: z.string().nullable(),
    }),
    submissionNo: z.number().int().positive(),
    sourceConnection: z.enum(["inst", "fapp"]),
  }),
  InstallChecked: z.object({
    submissionNo: z.number().int().positive(),
    results: z.array(
      z.object({
        ruleId: z.string(),
        result: z.enum(["pass", "fail", "not_applicable"]),
        facts: z.record(z.unknown()),
      }),
    ),
    blockingFailures: z.array(z.string()),
  }),
  InstallHeld: z.object({
    failedRuleIds: z.array(z.string()).min(1),
    findingIds: z.array(uuidSchema),
  }),
  InstallReturnedToInstaller: z.object({ failedChecks: z.array(z.string()), message: z.string() }),
  InstallAccepted: z.object({ registryEventId: uuidSchema }),
  InstallWithdrawn: z.object({ reason: z.string() }),
  InstallerChanged: z.object({
    name: z.string(),
    type: z.enum(["contractor", "in_house"]),
    active: z.boolean(),
  }),

  // 10. Platform
  ConnectionSyncStarted: z.object({
    connectionId: z.string(),
    trigger: z.enum(["schedule", "manual"]),
  }),
  ConnectionSyncCompleted: z.object({
    connectionId: z.string(),
    counts: z.record(z.number().int().nonnegative()),
    durationMs: z.number().int().nonnegative(),
  }),
  ConnectionSyncFailed: z.object({
    connectionId: z.string(),
    errorClass: z.string(),
    message: z.string(),
  }),
  ModuleEnabled: z.object({ moduleKey: z.string() }),
  ModuleDisabled: z.object({ moduleKey: z.string(), reason: z.string().nullable() }),
  RoleMappingChanged: z.object({
    directoryGroup: z.string(),
    oldRole: z.string().nullable(),
    newRole: z.string().nullable(),
  }),
  UserSignedIn: z.object({
    method: z.literal("oidc"),
    mfa: z.literal(true),
    roles: z.array(z.string()),
  }),
  UserSignInFailed: z.object({ reason: z.enum(["no_mapped_group", "mfa_failed", "disabled"]) }),
} as const;

export type EventType = keyof typeof eventPayloadSchemas;
export const eventTypes = Object.keys(eventPayloadSchemas) as EventType[];
export const eventTypeSchema = z.enum(eventTypes as [EventType, ...EventType[]]);

export type EventPayload<T extends EventType> = z.infer<(typeof eventPayloadSchemas)[T]>;

/**
 * Evidence an event must carry to be accepted (contracts/events.md, Evidence
 * column). `required` lists the kinds of which at least one must be attached;
 * `requiredWhenSource` applies only when the event's source matches.
 */
export const eventEvidenceRules: Partial<
  Record<
    EventType,
    { required?: EvidenceKind[]; requiredWhenSource?: { source: "user"; kinds: EvidenceKind[] } }
  >
> = {
  SupplyPointAttributeChanged: {
    requiredWhenSource: {
      source: "user",
      kinds: ["document", "field_record", "photo", "correspondence"],
    },
  },
  PremisesAttributeChanged: {
    requiredWhenSource: {
      source: "user",
      kinds: ["document", "field_record", "photo", "correspondence"],
    },
  },
  MeterAttributeChanged: {
    requiredWhenSource: {
      source: "user",
      kinds: ["document", "field_record", "photo", "correspondence"],
    },
  },
  PremisesMerged: { required: ["document"] },
  MeterExchanged: { required: ["field_record", "read_record"] },
  MeterRemoved: { required: ["field_record"] },
  MeterLocationVerified: { required: ["field_record", "photo"] },
  AttributeConflictResolved: {
    required: [
      "document",
      "field_record",
      "photo",
      "read_record",
      "correspondence",
      "market_response",
    ],
  },
  ReadRolloverConfirmed: { required: ["photo"] },
  ReadWithdrawn: { required: ["document"] },
  FieldJobCompleted: { required: ["field_record"] },
  BilateralMarkedReady: { required: ["photo", "field_record", "read_record", "document"] },
  InstallAccepted: { required: ["photo"] },
};

/** The common envelope every event carries (contracts/events.md). */
export const eventEnvelopeSchema = z.object({
  id: uuidSchema,
  seq: z.number().int().positive(),
  tenantId: uuidSchema,
  type: eventTypeSchema,
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  payload: z.record(z.unknown()),
  actor: z.string().min(1),
  source: sourceSchema,
  evidenceIds: z.array(uuidSchema),
  occurredAt: isoDateTimeSchema,
  recordedAt: isoDateTimeSchema,
  prevHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  isSynthetic: z.boolean(),
});
export type EventEnvelope = z.infer<typeof eventEnvelopeSchema>;

/** Validates a payload against its event type's schema. */
export function parseEventPayload<T extends EventType>(type: T, payload: unknown): EventPayload<T> {
  return eventPayloadSchemas[type].parse(payload);
}
